/*
 * <track-list> — "미리 듣기" (preview listening) playlist on the PDP.
 * Port of the Hydrogen storefront preview playlist (shopify-en-store-front →
 * products.$productHandle / products.ooak.$productHandle client fetch). See
 * snippets/track-list.liquid and MIGRATION_NOTES.md #4.
 *
 * Reads `data-album-id` (product metafield custom.album_id) and fetches, no auth:
 *   GET {base}/v1/web/discover/kitalbum/info/{album_id}
 *   → { data: { songPlayAvailable, songs: [ { songId, songTitle, songPlay } ] } }
 * The base is resolved from the hostname the same way script-gnb-user-type.js does it
 * (env.ENV is unavailable client-side): *.kitbetter.com → api.kittor.com, everything else
 * → api-test.kittor.com. `data-api-base` overrides both, and is empty unless the merchant
 * fills the section setting — the hostname is the default so a fresh publish cannot ship
 * production pointing at the test API.
 * Renders one track row per song (title + shared artist + play/pause), driving a
 * single shared <audio> so only one track plays at a time. songPlayAvailable === 0
 * (or no songs) shows the empty state; a 404 / network / CORS error removes the
 * element so the PDP degrades gracefully.
 */
if (!customElements.get('track-list')) {
  // Block-scoped on purpose: script-gnb-user-type.js declares the same two names at the top
  // level of a classic script, and the header ships it on every page — a second global `const`
  // of either name would be a redeclaration SyntaxError that kills this whole file.
  const RELEASE_BASE_URL = 'https://api.kittor.com';
  const TEST_BASE_URL = 'https://api-test.kittor.com';

  customElements.define(
    'track-list',
    class TrackList extends HTMLElement {
      constructor() {
        super();

        this.albumId = this.dataset.albumId;
        const { hostname } = window.location;
        const isProduction = hostname === 'kitbetter.com' || hostname.endsWith('.kitbetter.com');
        this.apiBase = (this.dataset.apiBase || (isProduction ? RELEASE_BASE_URL : TEST_BASE_URL)).replace(/\/+$/, '');
        this.artist = this.dataset.artist || '';
        this.strings = this.parseStrings();

        this.audio = this.querySelector('.pdp__track-list-audio');
        this.body = this.querySelector('[data-track-list-body]');
        this.countEl = this.querySelector('[data-track-count]');
        this.rowTemplate = this.querySelector('[data-track-template]');
        this.emptyTemplate = this.querySelector('[data-empty-template]');

        this.selectedIndex = -1;
        this.isPlaying = false;

        if (this.audio) {
          this.audio.addEventListener('ended', () => this.onEnded());
        }

        if (this.albumId) this.fetchSongs();
      }

      parseStrings() {
        try {
          const raw = JSON.parse(this.querySelector('[data-track-list-strings]').textContent);
          // Liquid's `t` filter HTML-escapes output and a <script> body is raw text the
          // HTML parser does not entity-decode — so decode the values here (matches fan-sign).
          const decoder = document.createElement('textarea');
          const decode = (value) => {
            decoder.innerHTML = value;
            return decoder.value;
          };
          return Object.keys(raw).reduce((acc, key) => {
            acc[key] = typeof raw[key] === 'string' ? decode(raw[key]) : raw[key];
            return acc;
          }, {});
        } catch (e) {
          return {};
        }
      }

      /* ---- Data ------------------------------------------------------------ */

      async fetchSongs() {
        try {
          const res = await fetch(`${this.apiBase}/v1/web/discover/kitalbum/info/${encodeURIComponent(this.albumId)}`);
          if (!res.ok) return this.dismiss(); // 404 / server error → not shown
          const json = await res.json();
          const data = json && json.data;
          if (!data) return this.dismiss();
          const songs = data.songPlayAvailable === 0 ? [] : data.songs || [];
          this.render(songs);
        } catch (e) {
          // Network / CORS (e.g. non-whitelisted origin) → hide gracefully.
          this.dismiss();
        }
      }

      dismiss() {
        this.remove();
      }

      /* ---- Render ---------------------------------------------------------- */

      render(songs) {
        this.songs = songs;
        this.body.textContent = '';

        if (!songs.length) {
          this.classList.add('pdp__track-list--empty');
          if (this.emptyTemplate) this.body.appendChild(this.emptyTemplate.content.cloneNode(true));
          this.hidden = false;
          return;
        }

        if (this.countEl) this.countEl.textContent = this.formatCount(songs.length);

        songs.forEach((song, index) => {
          if (index > 0) {
            const divider = document.createElement('span');
            divider.className = 'pdp__track-list-divider';
            divider.setAttribute('role', 'separator');
            this.body.appendChild(divider);
          }
          this.body.appendChild(this.createRow(song, index));
        });

        if (songs.length > 3) {
          this.classList.add('pdp__track-list--scroll');
          this.body.addEventListener('scroll', () => this.updateMask(), { passive: true });
          requestAnimationFrame(() => this.updateMask());
        }

        this.hidden = false;
      }

      createRow(song, index) {
        const frag = this.rowTemplate.content.cloneNode(true);
        const btn = frag.querySelector('[data-track-item]');
        frag.querySelector('[data-track-num]').textContent = index + 1;
        frag.querySelector('[data-track-title]').textContent = song.songTitle || '';
        frag.querySelector('[data-track-artist]').textContent = this.artist;

        btn.dataset.index = index;
        if (song.songPlay) {
          btn.setAttribute('aria-label', this.strings.play || 'Play');
          btn.addEventListener('click', () => this.onToggle(index, song));
        } else {
          // No preview available → disabled (greyed), matches Figma disable state.
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        }
        return frag;
      }

      formatCount(n) {
        const template = n === 1 ? this.strings.track_count_one : this.strings.track_count_other;
        return (template || '{count}').replace('{count}', n);
      }

      /* ---- Playback -------------------------------------------------------- */

      onToggle(index, song) {
        if (!this.audio) return;

        if (index === this.selectedIndex) {
          if (this.isPlaying) {
            this.audio.pause();
            this.setPlaying(false);
          } else {
            this.play();
          }
          return;
        }

        // Switch to a different track: rebind the single shared <audio> source.
        this.selectedIndex = index;
        this.audio.src = song.songPlay;
        this.audio.load();
        this.play();
      }

      /**
       * sample_play_shop — reference: routes/($locale).products.$productHandle/index.tsx:654
       * and :669, which fire it immediately after audio.play() on both branches of the
       * toggle. Both branches land here, so one call covers them; a pause/resume of the same
       * track is a play in the reference too.
       */
      play() {
        const promise = this.audio.play();
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        this.setPlaying(true);

        const song = (this.songs || [])[this.selectedIndex];
        if (song) {
          window.GTM?.samplePlayShop({
            album_id: this.albumId,
            title_code: song.songId,
            title_nm: song.songTitle,
          });
        }
      }

      onEnded() {
        this.audio.currentTime = 0;
        this.setPlaying(false);
      }

      setPlaying(playing) {
        this.isPlaying = playing;
        this.updateActiveRow();
      }

      updateActiveRow() {
        this.body.querySelectorAll('[data-track-item]').forEach((el) => {
          const active = Number(el.dataset.index) === this.selectedIndex && this.isPlaying;
          el.classList.toggle('pdp__track-list-item--playing', active);
          if (!el.disabled) {
            const label = active ? this.strings.pause : this.strings.play;
            if (label) el.setAttribute('aria-label', label);
          }
        });
      }

      /* ---- Scroll fade mask (4+ tracks) ------------------------------------ */

      updateMask() {
        const b = this.body;
        const atTop = b.scrollTop <= 1;
        const atBottom = b.scrollTop + b.clientHeight >= b.scrollHeight - 1;
        b.style.setProperty('--track-list-fade-top', atTop ? '0rem' : '2.4rem');
        b.style.setProperty('--track-list-fade-bottom', atBottom ? '0rem' : '2.4rem');
      }
    }
  );
}
